<template>
  <v-row>
    <v-col cols="12">
      <v-card outlined :style="{ overflow: 'hidden' }">
        <LoadingArea :loading="savingModule" class="px-2 pt-2 pb-4">
          <v-card-title
            class="mx-n2 mt-n2 px-8 primary white--text justify-space-between"
          >
            <span>{{ pageTitle }}</span>
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  class="text-none"
                  color="white"
                  @click="$emit('goBackClicked')"
                  v-on="on"
                  v-bind="attrs"
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </template>
              {{ $t("closeMsg") }}
            </v-tooltip>
          </v-card-title>

          <div class="pa-6">
            <v-row justify="space-between" no-gutters class="">
              <v-col cols="auto">
                <div class="text-h6">{{ $t("moduleDetailsMsg") }}</div>
              </v-col>
            </v-row>

            <v-form ref="moduleDetailsForm">
              <v-row class="mt-4">
                <v-col cols="8">
                  <div>
                    {{ $t("moduleNameMsg") }}&nbsp<span class="error--text"
                      >*</span
                    >
                  </div>
                  <v-input
                    :rules="moduleInputRules"
                    :value="module.ui_content"
                    hide-details="auto"
                  >
                    <div :style="{ width: '100%' }">
                      <div
                        id="module-quill-editor"
                        class="quill-container"
                        :style="{ height: '100px' }"
                      ></div>
                    </div>
                    <template #message="{ message }">
                      <div class="ml-3">{{ message }}</div>
                    </template>
                  </v-input>
                </v-col>
              </v-row>
            </v-form>

            <v-row class="mt-7">
              <v-col cols="12">
                <v-divider />
              </v-col>
            </v-row>

            <v-row class="mt-7">
              <v-col cols="auto">
                <div class="text-h6">{{ $tc("questionMsg", 2) }}</div>
              </v-col>
            </v-row>

            <v-form ref="questionsForm">
              <v-row align="start">
                <v-col cols="3">
                  <v-btn
                    slot="header"
                    depressed
                    class="text-none"
                    color="success"
                    @click="onAddQuestionClicked"
                  >
                    {{ $t("addQuestionMsg") }}
                    <v-icon right> mdi-plus-circle-outline </v-icon>
                  </v-btn>

                  <div
                    v-if="questionsNotEmpty"
                    class="text-caption mt-4"
                    :style="{ color: 'rgba(0,0,0,0.6)' }"
                  >
                    {{ $t("faqQuestionHelpMsg") }}
                  </div>

                  <v-card
                    v-if="questionsNotEmpty"
                    outlined
                    class="mt-4"
                    :style="{ overflow: 'hidden' }"
                  >
                    <v-list class="py-0">
                      <v-list-item-group>
                        <Draggable
                          v-model="module.questions"
                          draggable=".draggable"
                        >
                          <v-list-item
                            v-for="(question, index) in questions"
                            :key="index"
                            :class="{
                              'draggable list-item': true,
                              'primary white--text':
                                questionIsSelected(question),
                            }"
                            active-class="white"
                            @focus="setQuestionOfInterest(question)"
                          >
                            <v-list-item-content>
                              <v-list-item-title class="text-body-2">{{
                                question.question
                              }}</v-list-item-title>
                            </v-list-item-content>

                            <v-tooltip top>
                              <template #activator="{ on, attrs }">
                                <v-list-item-icon v-on="on" v-bind="attrs">
                                  <v-btn
                                    x-small
                                    icon
                                    :color="
                                      questionIsSelected(question)
                                        ? 'white'
                                        : 'error'
                                    "
                                    @click="onRemoveQuestionClicked(question)"
                                  >
                                    <v-icon> mdi-close </v-icon>
                                  </v-btn>
                                </v-list-item-icon>
                              </template>
                              <span>{{ $t("removeQuestionMsg") }}</span>
                            </v-tooltip>
                          </v-list-item>
                        </Draggable>
                      </v-list-item-group>
                    </v-list>
                  </v-card>
                </v-col>

                <v-col v-show="questionCurrentlySelected" cols="9">
                  <div class="d-flex">
                    <v-divider vertical inset class="mr-4" />
                    <div class="flex-grow-1">
                      <v-row v-if="questionCurrentlySelected">
                        <v-col cols="12">
                          <div>
                            {{ $tc("questionMsg", 1) }}&nbsp<span
                              class="error--text"
                              >*</span
                            >
                          </div>
                          <v-text-field
                            class="text_field background"
                            outlined
                            dense
                            :rules="questionInputRules"
                            v-model="questionOfInterest.question"
                          ></v-text-field>
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="12">
                          <div class="mb-2">{{ $t("responseMsg") }}</div>
                          <v-input
                            :rules="responseInputRules"
                            :value="
                              questionOfInterest
                                ? questionOfInterest.response
                                : ''
                            "
                            hide-details="auto"
                          >
                            <div
                              :style="{
                                width: '100%',
                              }"
                            >
                              <div
                                id="quill-editor"
                                class="quill-container"
                                :style="{
                                  height: '375px',
                                }"
                              ></div>
                            </div>
                            <template #message="{ message }">
                              <div class="ml-3">
                                {{ message }}
                              </div>
                            </template>
                          </v-input>
                        </v-col>
                      </v-row>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-form>

            <v-row class="mt-7">
              <v-col cols="12">
                <v-divider />
              </v-col>
            </v-row>

            <v-row justify="end">
              <v-col cols="auto">
                <v-btn
                  class="text-none"
                  depressed
                  color="success"
                  :loading="savingModule"
                  @click="onSaveModuleClicked"
                >
                  <span>{{ $t("createMessage") }}</span>
                  <v-icon right>mdi-content-save</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </LoadingArea>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import LoadingArea from "@/components/Utils/LoadingArea";
import Draggable from "vuedraggable";
import { getQuillEditor } from "./quill_editor";

const generalInputRules = [(v) => !!v || "Field is required"];
const generalQuillInputRules = [
  (v) => v !== "<p><br></p>" || "Field is required",
];

export default {
  components: {
    Draggable,
    LoadingArea,
  },
  props: {
    mode: {
      type: String,
      default: "create",
    },
    module: {
      type: Object,
      default: null,
    },
    savingModule: {
      type: Boolean,
      default: false,
    },
    fieldDefaults: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    questionOfInterest: null,
    quill: null,
    moduleQuill: null,
    moduleQuillErrorMessages: [],
  }),
  computed: {
    pageTitle() {
      const creatingModule = this.mode === "create";
      if (creatingModule) {
        return this.$t("createFaqModuleMsg");
      } else {
        return this.$t("editFaqModuleMsg");
      }
    },
    questionCurrentlySelected() {
      return !!this.questionOfInterest;
    },
    questions() {
      const validQuestions = this.module.questions.filter(
        (question) => !!!question.removed
      );
      return validQuestions;
    },
    questionsNotEmpty() {
      return this.questions.length > 0;
    },
    saveButtonText() {
      let buttonText = "";
      if (this.mode === "create") {
        buttonText = "Create";
      } else if (this.mode === "edit") {
        buttonText = "Save";
      }
      return buttonText;
    },
    moduleInputRules() {
      return [
        ...generalQuillInputRules,
        this.getShouldNotMatchDefaultRule(this.fieldDefaults.moduleName),
      ];
    },
    questionInputRules() {
      return [
        ...generalInputRules,
        this.getShouldNotMatchDefaultRule(this.fieldDefaults.question),
      ];
    },
    responseInputRules() {
      return [
        ...generalQuillInputRules,
        this.getShouldNotMatchDefaultRule(this.fieldDefaults.response),
      ];
    },
  },
  methods: {
    getEmptyQuestion() {
      const newQuestionPosition = this.questions.length + 1;
      return {
        order: newQuestionPosition,
        question: this.fieldDefaults.question,
        response: this.fieldDefaults.response,
        new: true,
        removed: false,
      };
    },

    onAddQuestionClicked() {
      const emptyQuestion = this.getEmptyQuestion();
      this.module.questions.push(emptyQuestion);
      this.setQuestionOfInterest(emptyQuestion);
    },

    setQuestionOfInterest(question) {
      if (!!question) {
        this.setQuillFromQuestion(question);
      }
      this.questionOfInterest = question;
    },

    validateCurrentQuestionOfInterest() {
      if (!!!this.questionOfInterest) {
        return;
      }
    },

    setQuill(quillGetter, html) {
      const editor = quillGetter();
      const quillDelta = editor.clipboard.convert(html);
      editor.setContents(quillDelta, "silent");
    },

    setModuleQuill() {
      const html = this.module.ui_content;
      this.setQuill(() => this.moduleQuill, html);
    },

    setQuillFromQuestion(question) {
      // https://stackoverflow.com/questions/46626633/how-do-you-insert-html-into-a-quilljs
      const html = question.response;
      this.setQuill(() => this.quill, html);
    },

    onModuleQuillChange() {
      const html = this.moduleQuill.root.innerHTML;
      this.module.ui_content = html;
    },

    onQuillChange() {
      if (this.questionIsSelected) {
        const html = this.quill.root.innerHTML;
        this.questionOfInterest.response = html;
      }
    },

    questionIsSelected(question) {
      return this.questionOfInterest === question;
    },

    setQuillEditors() {
      this.moduleQuill = getQuillEditor({
        elementSelector: "#module-quill-editor",
      });
      this.moduleQuill.on("text-change", () => {
        this.onModuleQuillChange();
      });
      this.quill = getQuillEditor({ elementSelector: "#quill-editor" });
      this.quill.on("text-change", () => {
        this.onQuillChange();
      });
    },

    setFirstQuestionAsQuestionOfInterest() {
      const firstQuestion = this.questions?.[0];
      this.setQuestionOfInterest(firstQuestion);
    },

    onRemoveQuestionClicked(questionToRemove) {
      const questionIsNew = questionToRemove.new === true;
      const questionToRemoveIsSelected =
        this.questionIsSelected(questionToRemove);
      if (questionToRemoveIsSelected) {
        this.questionOfInterest = null;
      }
      if (questionIsNew) {
        const itemIndex = this.module.questions.indexOf(questionToRemove);
        this.module.questions.splice(itemIndex, 1);
      } else {
        questionToRemove.removed = true;
      }
    },

    onSaveModuleClicked() {
      const moduleFormValid = this.$refs.moduleDetailsForm.validate();
      if (this.validateQuestions() !== true) {
        return;
      }
      if (!moduleFormValid) {
        this.scrollToError();
        return;
      }
      this.$emit("saveModuleClicked", {
        module: this.module,
        mode: this.mode,
      });
    },

    validateQuestions() {
      for (const question of this.questions) {
        for (const questionRule of this.questionInputRules) {
          if (questionRule(question.question) !== true) {
            return this.validateQuestionsForm(question);
          }
        }
        for (const responseRule of this.responseInputRules) {
          if (responseRule(question.response) !== true) {
            return this.validateQuestionsForm(question);
          }
        }
      }
      return true;
    },

    validateQuestionsForm(question) {
      this.setQuestionOfInterest(question);
      const formValid = this.$refs.questionsForm;
      return formValid;
    },

    sortQuestions(questions) {
      return questions.sort((a, b) => {
        if (a.order < b.order) {
          return -1;
        } else if (a.order > b.order) {
          return 1;
        }
        return 0;
      });
    },

    scrollToError() {
      const errorElement = this.$refs.moduleDetailsForm.$el.querySelector(
        ".v-messages.error--text"
      );
      const scrollTo = errorElement.closest(".v-input");
      scrollTo.scrollIntoView({ block: "center" });
    },

    getShouldNotMatchDefaultRule(fieldDefault) {
      return (v) => {
        const regex = new RegExp(fieldDefault);
        return !!!v.match(regex) || this.$t("pleaseRemoveDefaultTextMsg");
      };
    },
  },
  mounted() {
    this.sortQuestions(this.module.questions);
    this.setQuillEditors();
    this.setModuleQuill();
    this.setFirstQuestionAsQuestionOfInterest();
  },
  watch: {
    questions(newVal) {
      newVal.forEach((question, index) => {
        question.order = index + 1;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.list-item:before {
  opacity: 0 !important;
}

::v-deep .quill-container {
  // & .ql-editor{
  //     line-height: 1.42;
  // }
  & p {
    margin-bottom: 2px;
  }
}
</style>
